import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import {
  RedditMentionDto,
  GetAllMentionsQuery,
  MappedBusinessMentionsResponse,
  ConfirmMentionResponse,
  DismissMentionResponse
} from '../../domain/entities/RedditMention';
import { PaginatedResult } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';
import { RedditMentionService } from '../services/RedditMentionService';
import { RedditMentionRepository } from '../../infrastructure/repositories/RedditMentionRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';

/**
 * Service instance (singleton)
 */
const mentionRepository = new RedditMentionRepository(apiClient);
const mentionService = new RedditMentionService(mentionRepository);

/**
 * Verify mention mutation params
 */
interface VerifyMentionParams {
  mentionId: string;
  businessId: string;
  userRole: UserRole;
}

/**
 * Reject mention mutation params
 */
interface RejectMentionParams {
  mentionId: string;
  reason?: string;
  userRole: UserRole;
}

/**
 * Delete mention mutation params
 */
interface DeleteMentionParams {
  mentionId: string;
  userRole: UserRole;
}

/**
 * Fetch mentions query hook
 *
 * @param userRole - Current user's role
 * @param userBusinessId - Current user's business ID (for customers)
 * @param query - Query filters (pagination, status, etc.)
 * @param enabled - Whether the query should run (default: true)
 */
export const useMentionsQuery = (
  userRole: UserRole,
  userBusinessId: string | null,
  query?: GetAllMentionsQuery,
  enabled: boolean = true
): UseQueryResult<PaginatedResult<RedditMentionDto> | MappedBusinessMentionsResponse, Error> => {
  return useQuery({
    queryKey: queryKeys.mentions.list(query),
    queryFn: async (): Promise<PaginatedResult<RedditMentionDto> | MappedBusinessMentionsResponse> => {
      return mentionService.getMentionsForUser(userRole, userBusinessId, query);
    },
    staleTime: CACHE_CONFIG.MENTIONS.staleTime,
    gcTime: CACHE_CONFIG.MENTIONS.gcTime,
    enabled
  });
};

/**
 * Verify mention mutation hook
 *
 * Updates the mention status to "Verified" and associates it with a business
 */
export const useVerifyMentionMutation = (): UseMutationResult<void, Error, VerifyMentionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mentionId, businessId, userRole }: VerifyMentionParams): Promise<void> => {
      return mentionService.processMentionVerification(mentionId, businessId, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Invalidate the specific mention detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.detail(variables.mentionId) });

      // Show success notification
      notificationService.showSuccess('Mention has been verified successfully', 'Mention Verified');
    },
    onError: (error: Error): void => {
      // Error notification already shown by ApiClient, but we can log here
      console.error('Verify mention failed:', error);
    }
  });
};

/**
 * Reject mention mutation hook
 *
 * Updates the mention status to "Rejected"
 */
export const useRejectMentionMutation = (): UseMutationResult<void, Error, RejectMentionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mentionId, reason, userRole }: RejectMentionParams): Promise<void> => {
      return mentionService.rejectMention(mentionId, reason, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Invalidate the specific mention detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.detail(variables.mentionId) });

      // Show success notification
      notificationService.showSuccess('Mention has been rejected', 'Mention Rejected');
    },
    onError: (error: Error): void => {
      console.error('Reject mention failed:', error);
    }
  });
};

/**
 * Delete mention mutation hook (admin only)
 *
 * Permanently deletes the mention
 */
export const useDeleteMentionMutation = (): UseMutationResult<void, Error, DeleteMentionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mentionId, userRole }: DeleteMentionParams): Promise<void> => {
      return mentionService.deleteMention(mentionId, userRole);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Remove the specific mention from cache
      void queryClient.removeQueries({ queryKey: queryKeys.mentions.detail(variables.mentionId) });

      // Show success notification
      notificationService.showSuccess('Mention has been deleted successfully', 'Mention Deleted');
    },
    onError: (error: Error): void => {
      console.error('Delete mention failed:', error);
    }
  });
};

/**
 * Confirm mention mutation params
 * Session 003: New endpoint for confirming mentions
 */
interface ConfirmMentionParams {
  mentionId: string;
  businessId: string;
}

/**
 * Confirm mention mutation hook (Admin only - Session 003)
 *
 * Confirms a mention and links it to a specific business
 * Uses: POST /api/reddit-mentions/{id}/confirm
 */
export const useConfirmMention = (): UseMutationResult<ConfirmMentionResponse, Error, ConfirmMentionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mentionId, businessId }: ConfirmMentionParams): Promise<ConfirmMentionResponse> => {
      return mentionRepository.confirm(mentionId, businessId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Invalidate the specific mention detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.detail(variables.mentionId) });

      // Show success notification
      notificationService.showSuccess('Mention confirmed and linked to business', 'Mention Confirmed');
    },
    onError: (error: Error): void => {
      console.error('Confirm mention failed:', error);
      // Error notification already shown by ApiClient
    }
  });
};

/**
 * Dismiss mention mutation params
 * Session 003: New endpoint for dismissing mentions
 */
interface DismissMentionParams {
  mentionId: string;
}

/**
 * Dismiss mention mutation hook (Admin only - Session 003)
 *
 * Dismisses a mention as a false positive
 * Uses: POST /api/reddit-mentions/{id}/dismiss
 */
export const useDismissMention = (): UseMutationResult<DismissMentionResponse, Error, DismissMentionParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mentionId }: DismissMentionParams): Promise<DismissMentionResponse> => {
      return mentionRepository.dismiss(mentionId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate all mention list queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.lists() });

      // Invalidate the specific mention detail if it's cached
      void queryClient.invalidateQueries({ queryKey: queryKeys.mentions.detail(variables.mentionId) });

      // Show success notification
      notificationService.showSuccess('Mention dismissed as false positive', 'Mention Dismissed');
    },
    onError: (error: Error): void => {
      console.error('Dismiss mention failed:', error);
      // Error notification already shown by ApiClient
    }
  });
};
